<template>
  <div v-if="hasQuotes">
    <QuoteSummary
      :cover="getMPPICover"
      :isShowChangeCover="false"
      :elevation="0"
      @click:backButton="back"
      @click="customiseQuote"
      class="mb-12"
      handle-back-btn-externally
      heading="Your Mortgage Protection quotes"
      backButtonText="Back to 'Cover details'"
    />

    <div class="d-flex flex-column align-center mt-15">
      <v-card outlined width="100%" max-width="454" class="quote-card">
        <v-card-title class="justify-center px-6 py-2 primary--text">
          <h1 class="font-16 font-weight-bold">Mortgage Protection cover</h1>
        </v-card-title>
        <v-card-text class="px-6 py-10 primary--text">
          <h2 class="font-24 mb-6">{{ coverType }}</h2>

          <div
            class="d-flex flex-column flex-md-row justify-md-space-between primary--text"
          >
            <div>
              <p class="font-40 font-weight-bold mb-0">
                {{ price | formatCurrency }}
              </p>
              <p class="font-16 font-weight-bold mb-2">
                {{ paymentFrequency }}
              </p>
              <a
                href="#"
                @click.prevent="
                  showPremiumBreakdown(selectedQuote, productGroup)
                "
                class="font-16"
                >View a breakdown</a
              >
            </div>
            <div class="order-first order-md-0 mb-6 mb-md-0">
              <v-img
                :src="
                  require('@/assets/images/insurers/' + insurer?.image + '.png')
                "
                :max-width="200"
                :alt="insurer?.name"
                contain
              ></v-img>
            </div>
          </div>

          <BaseButton
            class="font-20 font-weight-bold my-6"
            color="mustard"
            depressed
            height="60"
            rounded
            text="APPLY"
            width="100%"
            @click="applyForQuote"
          ></BaseButton>

          <WhatsIncluded :items="WHATS_INCLUDED.MPPI" />
        </v-card-text>
      </v-card>

      <div class="d-flex mt-6">
        <BaseButton
          text="Share quote"
          flat
          depressed
          ripple
          class="font-weight-bold mr-4"
          icon="mdi-share-variant"
          color="#106175"
          small
          @click="shareQuote(selectedQuote?.QuoteId)"
        />
        <BaseButton
          text="Print quote"
          flat
          depressed
          ripple
          class="font-weight-bold"
          icon="mdi-printer"
          color="#106175"
          @click="printQuote(selectedQuote)"
          small
        />
      </div>
    </div>

    <BadgeDefaqto :show="isShowBadgeDefaqto" />

    <BaseDialog
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
      ref="shareDialog"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="data?.content"
          :quoteId="data?.quoteId"
          :quoteRequestId="data?.quoteRequestId"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <BaseDialog
      contentClass="mb-0"
      headerClass="px-7 pt-6 pb-3"
      headerDivider
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      ref="printQuotes"
      windowClass="pa-0 pb-xl-0 mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <MPPIDrawerCustomiseCover
      v-model="isShowDrawerCustomiseCover"
      @closeDrawer="closeDrawer"
    ></MPPIDrawerCustomiseCover>
    <BaseDialog
      headerClass="mx-md-8 mx-4 mt-md-8 mt-6"
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0"
      contentClass="mb-2"
    >
      <template v-slot:content="{ data }">
        <MPPIPremiumBreakdownDialog
          :breakdown-model="data"
        ></MPPIPremiumBreakdownDialog>
      </template>
    </BaseDialog>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { scrollTop } from "@/common/utils";
import {
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
} from "@/store/types/module-types";
import { TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { ROUTE_NAME } from "@/constants/route.constants";
import { WHATS_INCLUDED } from "@/constants/whats-included.constants";
import { getProductGroup } from "@/common/question-set";

export default {
  name: "YourQuotesPage",
  components: {
    MPPIDrawerCustomiseCover: () =>
      import("@/views/MPPI/B2B/DrawerCustomiseCover.vue"),
    QuoteSummary: () => import("@/components/QuoteResult/QuoteSummary.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    BadgeDefaqto: () => import("@/components/QuoteResult/BadgeDefaqto.vue"),
    MPPIPremiumBreakdownDialog: () =>
      import("@/components/QuoteResult/MPPIPremiumBreakdownDialog.vue"),
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    WhatsIncluded: () => import("@/components/QuoteResult/WhatsIncluded.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      isShowDrawerCustomiseCover: false,
      isShowBadgeDefaqto: true,
      TOOL_TIP_TEXT,
      WHATS_INCLUDED,
    };
  },
  watch: {
    isShowDrawerCustomiseCover(val) {
      if (!val) {
        this.isShowBadgeDefaqto = true;
      }
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getMPPICover",
      "hasQuotes",
      "insurerQuotesModel",
      "qsId",
    ]),
    ...mapState(QUESTIONS_MODULE, ["overlay"]),
    insurer() {
      return this.insurerQuotesModel?.[0] || null;
    },
    selectedQuote() {
      return this.insurer?.selectedQuote || null;
    },
    productGroup() {
      return getProductGroup(this.qsId);
    },
    coverType() {
      return this.getMPPICover?.type || "";
    },
    price() {
      return this.selectedQuote?.MonthlyTotal || "";
    },
    paymentFrequency() {
      return (
        this.selectedQuote?.Premiums?.[0]?.PaymentFrequencyDescription || ""
      );
    },
  },
  beforeMount() {
    scrollTop();
  },
  mounted() {
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "setOverlayVisible",
      "setRestoringLastStep",
      "refreshSessionAndNextStep",
    ]),
    ...mapActions(APPLICATION_MODULE, ["setApplicationActiveQuote"]),
    back() {
      this.$router.push({
        name: ROUTE_NAME.MPPI.B2B.COVER_DETAILS,
      });
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    showPremiumBreakdown(quote, productGroup) {
      this.showPremiumBreakdownMonthly(
        quote,
        productGroup,
        "Premium breakdown"
      );
    },
    applyForQuote() {
      this.setApplicationActiveQuote(this.insurerQuotesModel[0]);
      this.refreshSessionAndNextStep(ROUTE_NAME.MPPI.B2B.APPLICATION);
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: this.getMPPICover,
        quoteId: quoteId,
      });
    },
    async printQuote(quote) {
      try {
        this.setOverlayVisible(true);

        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({ docs, quote });

        data.files = data.files.filter(
          (item, index, self) =>
            index ===
            self.findIndex((t) => t.Code === item.Code && t.Name === item.Name)
        );

        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    customiseQuote() {
      this.isShowDrawerCustomiseCover = true;
      document.getElementsByTagName("html")[0].style.overflow = "hidden";
    },
    closeDrawer() {
      this.isShowDrawerCustomiseCover = false;
      document.getElementsByTagName("html")[0].style.overflow = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-card {
  border-color: var(--v-bordergrey-base);
  border-radius: 10px;

  .v-card__title {
    border-bottom: solid 1px var(--v-bordergrey-base);
    background-color: transparentize($secondary, 0.5);
  }

  .v-card__text {
    line-height: normal;
  }
}
</style>
